import React, { useState } from "react";
import {
  Box,
  Card,
  TextField,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  Link,
  Container,
  IconButton,
  InputAdornment,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  ArrowBack,
  Close,
} from "@mui/icons-material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import LogoImage from "../../assets/SJ-logo.png";
import DesignImage from "../../assets/login_design.png";
import HeroImage from "../../assets/login_hero.png";
import api from "../../api/api";
import CustomSnackbar from "../../components/CustomSnackbar";
import { useNavigate } from "react-router-dom";
import translationsMap from "../../en-ja-map";
const t = (k) => translationsMap?.[k] ?? k;

const theme = createTheme({
  palette: {
    primary: {
      main: "#ff0000",
    },
  },
});

const Login = () => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState("success");

  const [currentScreen, setCurrentScreen] = useState("login"); // 'login', 'forgotPassword', 'enterCode', 'resetPassword'
  const [otpFlow, setOtpFlow] = useState(""); // 'forgotPassword' or 'firstTimeLogin'
  const [firstTimeLoginCredentials, setFirstTimeLoginCredentials] =
    useState(null); // Store credentials for first-time login

  const [formData, setFormData] = useState({
    username: "",
    password: "",
    rememberMe: false,
    email: "",
    code: ["", "", "", "", "", ""],
    newPassword: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [verifyEmail, setVerifyEmail] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const navigate = useNavigate();

  const handleCloseSnackbar = () => {
    setSnackbarType("success");
    setSnackbarOpen(false);
    setSnackbarMessage("");
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleCodeChange = (index, value) => {
    if (value.length <= 1) {
      const newCode = [...formData.code];
      newCode[index] = value;
      setFormData((prev) => ({
        ...prev,
        code: newCode,
      }));

      if (value && index < 5) {
        const nextInput = document.getElementById(`code-input-${index + 1}`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (currentScreen === "login") {
      if (!formData.username.trim()) {
        newErrors.username = t("Username is required");
      } else if (formData.username.length < 3) {
        newErrors.username = t("Username must be at least 3 characters");
      }

      if (!formData.password.trim()) {
        newErrors.password = t("Password is required");
      } else if (formData.password.length < 6) {
        newErrors.password = t("Password must be at least 6 characters");
      }
    } else if (currentScreen === "forgotPassword") {
      if (!formData.email.trim()) {
        newErrors.email = t("Email is required");
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = t("Email is invalid");
      }
    } else if (currentScreen === "enterCode") {
      const codeString = formData.code.join("");
      if (codeString.length !== 6) {
        newErrors.code = t("Please enter all 6 digits");
      }
    } else if (currentScreen === "resetPassword") {
      const codeString = formData.code.join("");
      if (codeString.length !== 6) {
        newErrors.code = t("Please enter all 6 digits");
      }

      if (!formData.newPassword.trim()) {
        newErrors.newPassword = t("New password is required");
      } else if (formData.newPassword.length < 6) {
        newErrors.newPassword = t("Password must be at least 6 characters");
      }

      if (!formData.confirmPassword.trim()) {
        newErrors.confirmPassword = t("Confirm password is required");
      } else if (formData.newPassword !== formData.confirmPassword) {
        newErrors.confirmPassword = t("Passwords do not match");
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFirstLogin = (credentials) => {
    setFirstTimeLoginCredentials(credentials);
    setFormData((prev) => ({
      ...prev,
      email: credentials.email,
    }));
    setOtpFlow("firstTimeLogin");
    setCurrentScreen("forgotPassword");
    setSnackbarMessage(t("Please verify your account with OTP before logging in"));
    setSnackbarOpen(true);
    setSnackbarType("info");
  };

  const loginUser = async (obj) => {
    api
      .post("v1/clinic/login", { email: obj.email, password: obj.password })
      .then((res) => {
        if (res.status === 200) {
          setSnackbarType("success");
          setSnackbarMessage(t("Successfully Logged In"));
          setSnackbarOpen(true);
          const token = res?.data?.token?.AccessToken;
          if (token) {
            localStorage.setItem("authToken", res?.data?.token?.AccessToken);
            localStorage.setItem("refreshToken", res.data?.token?.RefreshToken);
            localStorage.setItem("user", JSON.stringify(res?.data?.user));
            navigate("/dashboard");
            setLoginLoading(false);
          } else {
            setLoginLoading(false);
          }
        }
      })
      .catch((error) => {
        const message = error?.response?.data?.message;
        if (error?.response?.data?.message === "Incorrect username or password.") {
          setSnackbarMessage(t("Invalid Email Id or Password"));
          setSnackbarOpen(true);
          setSnackbarType("error");
        } else if (message === "User is not confirmed.") {
          handleFirstLogin(obj);
        } else {
          setSnackbarMessage(message || t("Login failed"));
          setSnackbarOpen(true);
          setSnackbarType("error");
        }
        setLoginLoading(false);
      });
  };

  const sendOTPForFirstTimeLogin = async (email) => {
    setVerifyEmail(email);
    return api
      .post("v1/clinic/resend-code", { username: email })
      .then(() => true)
      .catch(() => false);
  };

  const sendOTPForPasswordReset = async (email) => {
    return api
      .post("v1/clinic/forgot-password", { email: email })
      .then(() => true)
      .catch((err) => {
        setSnackbarMessage(t("Failed to send reset code. Please try again."));
        setSnackbarOpen(true);
        setSnackbarType("error");
        return false;
      });
  };

  const verifyOTPForFirstTimeLogin = async (code) => {
    return api
      .post("v1/clinic/confirm-code", { username: verifyEmail, code: code })
      .then((res) => (res?.status === 200 ? true : false))
      .catch(() => false);
  };

  const resetPasswordWithOTP = async (email, code, newPassword) => {
    return api
      .post("v1/clinic/confirm-forgot-password", {
        email: email,
        code: code,
        newPassword: newPassword,
        confirmPassword: newPassword,
      })
      .then((res) => (res?.status === 200 ? true : false))
      .catch((err) => {
        const message =
          err?.response?.data?.message ||
          t("Failed to reset password. Please try again.");
        setSnackbarMessage(message);
        setSnackbarOpen(true);
        setSnackbarType("error");
        return false;
      });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      if (currentScreen === "login") {
        const obj = {
          email: formData.username,
          password: formData.password,
        };
        setLoginLoading(true);
        loginUser(obj);
      } else if (currentScreen === "forgotPassword") {
        if (otpFlow === "firstTimeLogin") {
          const success = await sendOTPForFirstTimeLogin(formData.email);
          if (success) {
            setSnackbarMessage(t("OTP sent to your email for account verification"));
            setSnackbarOpen(true);
            setSnackbarType("success");
            setCurrentScreen("enterCode");
          }
        } else {
          const success = await sendOTPForPasswordReset(formData.email);
          if (success) {
            setSnackbarMessage(t("Password reset code sent to your email"));
            setSnackbarOpen(true);
            setSnackbarType("success");
            setCurrentScreen("resetPassword");
          }
        }
      } else if (currentScreen === "enterCode") {
        const codeString = formData.code.join("");
        const isValidCode = await verifyOTPForFirstTimeLogin(codeString);

        if (isValidCode) {
          setSnackbarMessage(t("Account verified successfully! Please login again."));
          setSnackbarOpen(true);
          setSnackbarType("success");

          setTimeout(() => {
            setCurrentScreen("login");
            setOtpFlow("");
            setFirstTimeLoginCredentials(null);
            setFormData({
              username: "",
              password: "",
              rememberMe: false,
              email: "",
              code: ["", "", "", "", "", ""],
              newPassword: "",
              confirmPassword: "",
            });
          }, 2000);
        } else {
          setSnackbarMessage(t("Invalid OTP. Please try again."));
          setSnackbarOpen(true);
          setSnackbarType("error");
        }
      } else if (currentScreen === "resetPassword") {
        const codeString = formData.code.join("");
        const success = await resetPasswordWithOTP(
          formData.email,
          codeString,
          formData.newPassword
        );

        if (success) {
          setSnackbarMessage(
            t("Password reset successfully! You can now login with your new password.")
          );
          setSnackbarOpen(true);
          setSnackbarType("success");

          setTimeout(() => {
            setCurrentScreen("login");
            setOtpFlow("");
            setFormData({
              username: formData.email,
              password: "",
              rememberMe: false,
              email: "",
              code: ["", "", "", "", "", ""],
              newPassword: "",
              confirmPassword: "",
            });
          }, 2000);
        }
      }
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleToggleNewPasswordVisibility = () => {
    setShowNewPassword((prev) => !prev);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword((prev) => !prev);
  };

  const handleForgotPassword = () => {
    setOtpFlow("forgotPassword");
    setCurrentScreen("forgotPassword");
    setFormData((prev) => ({
      ...prev,
      email: prev.username || "",
    }));
  };

  const handleBackToLogin = () => {
    setCurrentScreen("login");
    setOtpFlow("");
    setFirstTimeLoginCredentials(null);
    setErrors({});
  };

  const handleBackToForgotPassword = () => {
    setCurrentScreen("forgotPassword");
    setErrors({});
  };

  const renderLoginForm = () => (
    <>
      <Typography variant="h5" sx={{ mb: 1, fontWeight: 700, color: "#1a1a1a" }}>
        {t("Welcome Back")}
      </Typography>

      <Typography variant="body1" sx={{ mb: 4, color: "#666666", fontSize: "14px", lineHeight: 1.5 }}>
        {t("Enter your email and password to access your account")}
      </Typography>

      <form onSubmit={handleSubmit}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 1, color: "#1a1a1a", fontWeight: 500, fontSize: "14px" }}>
            {t("Username")}
          </Typography>
          <TextField
            fullWidth
            name="username"
            value={formData.username}
            onChange={handleInputChange}
            placeholder={t("Enter your Login ID")}
            variant="outlined"
            error={!!errors.username}
            helperText={errors.username}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                fontSize: "14px",
                "& fieldset": { borderColor: "#e0e0e0" },
                "&:hover fieldset": { borderColor: "#ff0000" },
                "&.Mui-focused fieldset": { borderColor: "#ff0000" },
              },
            }}
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 1, color: "#1a1a1a", fontWeight: 500, fontSize: "14px" }}>
            {t("Password")}
          </Typography>
          <TextField
            fullWidth
            name="password"
            type={showPassword ? "text" : "password"}
            value={formData.password}
            onChange={handleInputChange}
            placeholder={t("Enter your password")}
            variant="outlined"
            error={!!errors.password}
            helperText={errors.password}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleTogglePasswordVisibility} edge="end" aria-label="toggle password visibility" size="small">
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                fontSize: "14px",
                "& fieldset": { borderColor: "#e0e0e0" },
                "&:hover fieldset": { borderColor: "#ff0000" },
                "&.Mui-focused fieldset": { borderColor: "#ff0000" },
                '& input[type="password"]::-ms-reveal': { display: "none" },
                '& input[type="password"]::-ms-clear': { display: "none" },
              },
            }}
          />
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
          <FormControlLabel
            control={
              <Checkbox
                name="rememberMe"
                checked={formData.rememberMe}
                onChange={handleInputChange}
                size="small"
                sx={{
                  color: "#666",
                  "&.Mui-checked": { color: "#ff0000" },
                }}
              />
            }
            label={t("Remember me")}
            sx={{ "& .MuiFormControlLabel-label": { fontSize: "14px", color: "#666666" } }}
          />
          <Link
            onClick={handleForgotPassword}
            variant="body2"
            sx={{
              color: "#ff0000",
              textDecoration: "none",
              fontSize: "14px",
              fontWeight: 500,
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {t("Forgot Password")}
          </Link>
        </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loginLoading}
          sx={{
            py: 1.5,
            backgroundColor: "#ff0000",
            borderRadius: 2,
            textTransform: "none",
            fontSize: "16px",
            fontWeight: 600,
            boxShadow: "none",
            "&:hover": { backgroundColor: "#e60000", boxShadow: "none" },
            mb: 4,
          }}
        >
          {loginLoading ? t("Signing In .....") : t("Sign In")}
        </Button>
      </form>
    </>
  );

  const renderForgotPasswordForm = () => (
    <>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <IconButton onClick={handleBackToLogin} sx={{ mr: 1, p: 0.5 }}>
          <ArrowBack fontSize="small" />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 700, color: "#1a1a1a", fontSize: { xs: "1.5rem", sm: "1.75rem" }, flex: 1 }}>
          {otpFlow === "firstTimeLogin" ? t("Verify Account") : t("Reset Password")}
        </Typography>
      </Box>

      <Typography variant="body1" sx={{ mb: 4, color: "#666666", fontSize: "14px", lineHeight: 1.5 }}>
        {otpFlow === "firstTimeLogin"
          ? t("Verify your account to complete the login process")
          : t("Enter your email to receive a password reset code")}
      </Typography>

      <form onSubmit={handleSubmit}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 1, color: "#1a1a1a", fontWeight: 500, fontSize: "14px" }}>
            {t("Username")}
          </Typography>
          <TextField
            fullWidth
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder={t("Enter your Login ID")}
            variant="outlined"
            error={!!errors.email}
            helperText={errors.email}
            disabled={otpFlow === "firstTimeLogin"}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                fontSize: "14px",
                backgroundColor: "#f8f9fa",
                "& fieldset": { borderColor: "#e0e0e0" },
                "&:hover fieldset": { borderColor: "#ff0000" },
                "&.Mui-focused fieldset": { borderColor: "#ff0000" },
              },
            }}
          />
        </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{
            py: 1.5,
            backgroundColor: "#ff0000",
            borderRadius: 2,
            textTransform: "none",
            fontSize: "16px",
            fontWeight: 600,
            boxShadow: "none",
            "&:hover": { backgroundColor: "#e60000", boxShadow: "none" },
            mb: 4,
          }}
        >
          {otpFlow === "firstTimeLogin" ? t("Send OTP") : t("Send Reset Code")}
        </Button>
      </form>
    </>
  );

  const renderEnterCodeForm = () => (
    <>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <IconButton onClick={handleBackToForgotPassword} sx={{ mr: 1, p: 0.5 }}>
          <ArrowBack fontSize="small" />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 700, color: "#1a1a1a", fontSize: { xs: "1.5rem", sm: "1.75rem" }, flex: 1 }}>
          {t("Enter OTP")}
        </Typography>
        <IconButton onClick={handleBackToLogin} sx={{ p: 0.5 }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <Typography variant="body1" sx={{ mb: 4, color: "#666666", fontSize: "14px", lineHeight: 1.5 }}>
        {t("Enter the OTP sent to your email for account verification")}
      </Typography>

      <form onSubmit={handleSubmit}>
        <Box sx={{ display: "flex", gap: 2, mb: 2, justifyContent: "center" }}>
          {formData.code.map((digit, index) => (
            <TextField
              key={index}
              id={`code-input-${index}`}
              value={digit}
              onChange={(e) => handleCodeChange(index, e.target.value)}
              variant="outlined"
              error={!!errors.code}
              inputProps={{
                maxLength: 1,
                style: { textAlign: "center", fontSize: "18px", fontWeight: 600 },
              }}
              sx={{
                width: "50px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "& fieldset": { borderColor: errors.code ? "#d32f2f" : "#e0e0e0" },
                  "&:hover fieldset": { borderColor: "#ff0000" },
                  "&.Mui-focused fieldset": { borderColor: "#ff0000" },
                },
              }}
            />
          ))}
        </Box>
        {errors.code && (
          <Typography variant="caption" sx={{ color: "#d32f2f", display: "block", textAlign: "center", mb: 2 }}>
            {errors.code}
          </Typography>
        )}

        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{
            py: 1.5,
            backgroundColor: "#ff0000",
            borderRadius: 2,
            textTransform: "none",
            fontSize: "16px",
            fontWeight: 600,
            boxShadow: "none",
            "&:hover": { backgroundColor: "#e60000", boxShadow: "none" },
            mb: 4,
          }}
        >
          {t("Verify Account")}
        </Button>
      </form>
    </>
  );

  const renderResetPasswordForm = () => (
    <>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <IconButton onClick={handleBackToForgotPassword} sx={{ mr: 1, p: 0.5 }}>
          <ArrowBack fontSize="small" />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 700, color: "#1a1a1a", fontSize: { xs: "1.5rem", sm: "1.75rem" }, flex: 1 }}>
          {t("Reset Password")}
        </Typography>
        <IconButton onClick={handleBackToLogin} sx={{ p: 0.5 }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <Typography variant="body1" sx={{ mb: 4, color: "#666666", fontSize: "14px", lineHeight: 1.5 }}>
        {t("Enter the code sent to your email and set your new password")}
      </Typography>

      <form onSubmit={handleSubmit}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 1, color: "#1a1a1a", fontWeight: 500, fontSize: "14px" }}>
            {t("Verification Code")}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
            {formData.code.map((digit, index) => (
              <TextField
                key={index}
                id={`code-input-${index}`}
                value={digit}
                onChange={(e) => handleCodeChange(index, e.target.value)}
                variant="outlined"
                error={!!errors.code}
                inputProps={{
                  maxLength: 1,
                  style: { textAlign: "center", fontSize: "16px", fontWeight: 600 },
                }}
                sx={{
                  width: "45px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "& fieldset": { borderColor: errors.code ? "#d32f2f" : "#e0e0e0" },
                    "&:hover fieldset": { borderColor: "#ff0000" },
                    "&.Mui-focused fieldset": { borderColor: "#ff0000" },
                  },
                }}
              />
            ))}
          </Box>
          {errors.code && (
            <Typography variant="caption" sx={{ color: "#d32f2f", display: "block", textAlign: "center", mt: 1 }}>
              {errors.code}
            </Typography>
          )}
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 1, color: "#1a1a1a", fontWeight: 500, fontSize: "14px" }}>
            {t("New Password")}
          </Typography>
          <TextField
            fullWidth
            name="newPassword"
            type={showNewPassword ? "text" : "password"}
            value={formData.newPassword}
            onChange={handleInputChange}
            placeholder={t("Enter your new password")}
            variant="outlined"
            error={!!errors.newPassword}
            helperText={errors.newPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleToggleNewPasswordVisibility}
                    edge="end"
                    aria-label="toggle new password visibility"
                    size="small"
                  >
                    {showNewPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                fontSize: "14px",
                "& fieldset": { borderColor: "#e0e0e0" },
                "&:hover fieldset": { borderColor: "#ff0000" },
                "&.Mui-focused fieldset": { borderColor: "#ff0000" },
              },
            }}
          />
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="body2" sx={{ mb: 1, color: "#1a1a1a", fontWeight: 500, fontSize: "14px" }}>
            {t("Confirm Password")}
          </Typography>
          <TextField
            fullWidth
            name="confirmPassword"
            type={showConfirmPassword ? "text" : "password"}
            value={formData.confirmPassword}
            onChange={handleInputChange}
            placeholder={t("Confirm your new password")}
            variant="outlined"
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleToggleConfirmPasswordVisibility}
                    edge="end"
                    aria-label="toggle confirm password visibility"
                    size="small"
                  >
                    {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                fontSize: "14px",
                "& fieldset": { borderColor: "#e0e0e0" },
                "&:hover fieldset": { borderColor: "#ff0000" },
                "&.Mui-focused fieldset": { borderColor: "#ff0000" },
              },
            }}
          />
        </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{
            py: 1.5,
            backgroundColor: "#ff0000",
            borderRadius: 2,
            textTransform: "none",
            fontSize: "16px",
            fontWeight: 600,
            boxShadow: "none",
            "&:hover": { backgroundColor: "#e60000", boxShadow: "none" },
            mb: 4,
          }}
        >
          {t("Reset Password")}
        </Button>
      </form>
    </>
  );

  return (
    <ThemeProvider theme={theme}>
      <CustomSnackbar
        isOpen={snackbarOpen}
        message={snackbarMessage}
        onClose={handleCloseSnackbar}
        type={snackbarType}
      />
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: "#f5f7fa",
          position: "relative",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box sx={{ position: "absolute", top: { xs: 20, md: 40 }, left: { xs: 20, md: 40 }, zIndex: 2 }}>
          <img src={LogoImage} alt={t("Shop Japan Logo")} style={{ height: "40px", width: "auto" }} />
        </Box>

        <Box
          sx={{
            position: "absolute",
            left: { xs: "-100px", sm: "80px", md: "150px", lg: "200px" },
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 1,
            display: { xs: "none", sm: "block" },
          }}
        >
          <img src={HeroImage} alt={t("Medical Illustration")} style={{ width: "400px", height: "auto", maxWidth: "90vw" }} />
        </Box>

        <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "220px", zIndex: 0 }}>
          <img src={DesignImage} alt={t("Decorative Pattern")} style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 1 }} />
        </Box>

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 3 }}>
          <Box sx={{ display: "flex", justifyContent: { xs: "center", md: "flex-end" }, alignItems: "center", minHeight: "100vh", pr: { md: 8, lg: 12 } }}>
            <Box sx={{ width: "100%", maxWidth: "420px", display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Typography variant="h4" sx={{ mb: 3, fontWeight: 700, color: "#1a1a1a", textAlign: "center" }}>
                {t("SJ Clinic Portal")}
              </Typography>

              <Card
                elevation={16}
                sx={{
                  p: { xs: 4, sm: 5 },
                  borderRadius: 3,
                  backgroundColor: "white",
                  boxShadow: "0px 16px 40px rgba(0, 0, 0, 0.12)",
                  width: "100%",
                  border: "1px solid rgba(0, 0, 0, 0.05)",
                }}
              >
                <Box sx={{ display: { xs: "flex", md: "none" }, justifyContent: "center", mb: 3 }}>
                  <img src={LogoImage} alt={t("Shop Japan Logo")} style={{ height: "32px", width: "auto" }} />
                </Box>

                {currentScreen === "login" && renderLoginForm()}
                {currentScreen === "forgotPassword" && renderForgotPasswordForm()}
                {currentScreen === "enterCode" && renderEnterCodeForm()}
                {currentScreen === "resetPassword" && renderResetPasswordForm()}
              </Card>
            </Box>
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default Login;
